import type { SimpleDateDTO, VacationDTO } from "@wkt/api/checkin/checkinDTO";
import { DayType } from "@wkt/api/checkin/checkinENUM";

const COMPLETE_DAY_RECORD_TYPE = 7;
const HOURS_BAG_VACATION_ID = 6;

export type DayChipTone =
  | "accent"
  | "neutral"
  | "warning"
  | "vacation"
  | "paid-absence"
  | "unpaid-absence"
  | "rest"
  | "holiday";

export interface DayChip {
  key: string;
  label: string;
  tone: DayChipTone;
}

export interface DayChipCalendar {
  dayType?: number | null;
  checkinAllowed?: number | null;
  isCheckinAllowed?: number | null;
}

export interface DayChipCompany {
  defaultYearlyVacationLabel?: string | null;
  defaultYearlyExtraLabel?: string | null;
  defaultYearlyHoursBag1Label?: string | null;
  defaultYearlyHoursBag2Label?: string | null;
  defaultYearlyHoursBag3Label?: string | null;
  defaultYearlyHoursBag4Label?: string | null;
  defaultYearlyHoursBag5Label?: string | null;
}

function compareSimpleDates(
  left?: SimpleDateDTO | null,
  right?: SimpleDateDTO | null,
): number {
  if (!left || !right) {
    return 0;
  }

  const leftTime = new Date(left.year, left.month - 1, left.day).getTime();
  const rightTime = new Date(right.year, right.month - 1, right.day).getTime();
  return leftTime - rightTime;
}

export function isDateWithinVacation(
  date: SimpleDateDTO | null,
  vacation?: VacationDTO | null,
): boolean {
  if (!date || !vacation?.fromDate || !vacation?.toDate) {
    return false;
  }

  return (
    compareSimpleDates(date, vacation.fromDate) >= 0 &&
    compareSimpleDates(date, vacation.toDate) <= 0
  );
}

function getVacationByTypeBag(
  vacations: VacationDTO[],
  date: SimpleDateDTO | null,
  hoursBagType?: number | null,
): VacationDTO | null {
  return (
    vacations.find(
      (vacation) =>
        vacation?.recordType === COMPLETE_DAY_RECORD_TYPE &&
        vacation?.hoursBagType === hoursBagType &&
        isDateWithinVacation(date, vacation),
    ) ?? null
  );
}

function getVacationByType(
  vacations: VacationDTO[],
  date: SimpleDateDTO | null,
): VacationDTO | null {
  return (
    vacations.find(
      (vacation) =>
        vacation?.recordType === COMPLETE_DAY_RECORD_TYPE &&
        isDateWithinVacation(date, vacation),
    ) ?? null
  );
}

function getHoursBagLabel(
  hoursBagType: number,
  company: DayChipCompany | null | undefined,
  translate: (key: string) => string,
): string {
  if (!company) {
    return String(hoursBagType);
  }

  switch (hoursBagType) {
    case HOURS_BAG_VACATION_ID:
      return (
        company.defaultYearlyVacationLabel ||
        translate("wkt.client.common.check-card.chips.vacation")
      );
    case 7:
      return company.defaultYearlyExtraLabel || String(hoursBagType);
    case 1:
      return company.defaultYearlyHoursBag1Label || String(hoursBagType);
    case 2:
      return company.defaultYearlyHoursBag2Label || String(hoursBagType);
    case 3:
      return company.defaultYearlyHoursBag3Label || String(hoursBagType);
    case 4:
      return company.defaultYearlyHoursBag4Label || String(hoursBagType);
    case 5:
      return company.defaultYearlyHoursBag5Label || String(hoursBagType);
    default:
      return String(hoursBagType);
  }
}

export function resolveDayChips(
  date: SimpleDateDTO | null,
  calendar: DayChipCalendar | null | undefined,
  vacations: VacationDTO[],
  company: DayChipCompany | null | undefined,
  translate: (key: string) => string,
): DayChip[] {
  const chips: DayChip[] = [];
  const dayType = calendar?.dayType ?? DayType.REGULAR;

  if (dayType === DayType.REST) {
    chips.push({
      key: "rest",
      label: translate("wkt.client.common.check-card.chips.rest"),
      tone: "rest",
    });
  }

  if (dayType === DayType.NATIONAL_HOLIDAY) {
    chips.push({
      key: "national-holiday",
      label: translate("wkt.client.common.check-card.chips.holiday"),
      tone: "holiday",
    });
  }

  if (dayType === DayType.LOCAL_HOLIDAY) {
    chips.push({
      key: "local-holiday",
      label: translate("wkt.client.common.check-card.chips.local-holiday"),
      tone: "holiday",
    });
  }

  if (dayType === DayType.COMPANY_HOLIDAY) {
    chips.push({
      key: "company-holiday",
      label: translate("wkt.client.common.check-card.chips.free"),
      tone: "holiday",
    });
  }

  const paidAbsence = getVacationByTypeBag(vacations, date, null);
  if (paidAbsence) {
    chips.push({
      key: "absence-paid",
      label:
        paidAbsence.recordTitle ||
        translate("wkt.client.common.check-card.chips.absence_paid"),
      tone: "paid-absence",
    });
  }

  const vacation = getVacationByTypeBag(vacations, date, HOURS_BAG_VACATION_ID);
  if (vacation) {
    chips.push({
      key: "vacation",
      label:
        vacation.recordTitle ||
        getHoursBagLabel(HOURS_BAG_VACATION_ID, company, translate),
      tone: "vacation",
    });
  }

  const leave = getVacationByType(vacations, date);
  if (leave && !paidAbsence && !vacation) {
    const label =
      leave.hoursBagType != null
        ? getHoursBagLabel(leave.hoursBagType, company, translate)
        : leave.recordTitle ||
          translate("wkt.client.common.check-card.chips.leave");

    chips.push({
      key: "leave",
      label,
      tone: "unpaid-absence",
    });
  }

  const checkinAllowed =
    calendar?.checkinAllowed ?? calendar?.isCheckinAllowed ?? 1;
  if (checkinAllowed === 0) {
    chips.push({
      key: "no-checkin",
      label: translate("wkt.client.common.check-card.chips.no_checkin"),
      tone: "warning",
    });
  }

  return chips;
}
